import { useState, useEffect, useCallback } from 'react';
import { it, hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import { theme } from '../../theme';
import { SearchableSelect } from '../../components/common/SearchableSelect';

// ─── Constants ────────────────────────────────────────────────────────────────
const LICENSE_TYPES = ['perpetual', 'subscription', 'oem'];
const ASSIGNMENT_TYPES = [
  { value: 'per_user',   label: 'Per User',   desc: 'Assign to employees (e.g. Microsoft 365, Adobe CC)' },
  { value: 'per_device', label: 'Per Device',  desc: 'Assign to assets/hardware (e.g. Oracle DB, Windows Server)' },
  { value: 'mixed',      label: 'Mixed',       desc: 'Can be assigned to either users or devices' },
];
const STATUSES = ['active', 'expired', 'cancelled'];

const ASSIGNMENT_TYPE_COLORS = {
  per_user:   theme.info,
  per_device: '#8B5CF6',
  mixed:      theme.warning,
};

const STATUS_COLORS = {
  active:    theme.success,
  expired:   theme.danger,
  cancelled: theme.textMuted,
};

const EMPTY_FORM = {
  name: '', vendor: '', license_type: 'subscription', assignment_type: 'per_user',
  license_key: '', seats_total: 1, purchase_date: '', expiry_date: '',
  cost: '', notes: '', status: 'active',
};

const EMPTY_ASSIGN = { employee_id: '', asset_id: '', assigned_date: new Date().toISOString().slice(0, 10), notes: '' };

function fmt(v) { return (v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }
function fmtDate(v) { return v ? new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—'; }
function fmtCurrency(v) { return v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '—'; }

const s = {
  field:  { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label:  { fontSize: 12, fontWeight: 600, color: theme.text },
  input:  { padding: '8px 10px', borderRadius: 6, border: `1.5px solid ${theme.border}`, background: theme.cardBg, color: theme.text, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  grid3:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  pill:   (c) => ({ background: c + '22', color: c, border: `1px solid ${c}44`, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }),
  seatBar:(used, total) => {
    const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
    const color = pct >= 90 ? theme.danger : pct >= 70 ? theme.warning : theme.success;
    return (
      <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: 12, marginBottom: 3, color: theme.text }}>{used} / {total} used</div>
        <div style={{ height: 6, background: theme.border, borderRadius: 3 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .3s' }} />
        </div>
      </div>
    );
  },
};

// ─── Main Component ───────────────────────────────────────────────────────────
function Licenses() {
  const [licenses, setLicenses]       = useState([]);
  const [employees, setEmployees]     = useState([]);
  const [assets, setAssets]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterType, setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('active');

  // Modals
  const [formModal, setFormModal]     = useState(null); // null | license object (empty={} for new)
  const [form, setForm]               = useState(EMPTY_FORM);
  const [detailModal, setDetailModal] = useState(null); // license object
  const [assignModal, setAssignModal] = useState(null); // license object
  const [assignForm, setAssignForm]   = useState(EMPTY_ASSIGN);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterType)   params.assignment_type = filterType;
    if (filterStatus) params.status = filterStatus;
    try {
      const [lr, em, as] = await Promise.allSettled([
        it.getLicenses(params),
        hrms.getEmployees({ limit: 500 }),
        it.getAssets({}),
      ]);
      if (lr.status === 'fulfilled') setLicenses(lr.value.data || []);
      if (em.status === 'fulfilled') { const d = em.value.data; setEmployees(Array.isArray(d) ? d : d.items || []); }
      if (as.status === 'fulfilled') setAssets(as.value.data?.items || as.value.data || []);
    } finally { setLoading(false); }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // ── Save license ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { setError('Product name is required.'); return; }
    if (!form.seats_total || form.seats_total < 1) { setError('Seats must be ≥ 1.'); return; }
    setError(''); setSaving(true);
    const payload = {
      ...form,
      seats_total: parseInt(form.seats_total),
      cost: form.cost !== '' ? parseFloat(form.cost) : null,
      purchase_date: form.purchase_date || null,
      expiry_date:   form.expiry_date   || null,
      license_key:   form.license_key   || null,
      notes:         form.notes         || null,
    };
    try {
      if (formModal?.id) {
        await it.updateLicense(formModal.id, payload);
      } else {
        await it.createLicense(payload);
      }
      setFormModal(null); load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save license.');
    } finally { setSaving(false); }
  };

  // ── Assign ────────────────────────────────────────────────────────────────
  const handleAssign = async () => {
    setError('');
    const lic = assignModal;
    const payload = {
      assigned_date: assignForm.assigned_date,
      notes: assignForm.notes || null,
    };
    if (lic.assignment_type === 'per_user') {
      if (!assignForm.employee_id) { setError('Select an employee.'); return; }
      payload.employee_id = parseInt(assignForm.employee_id);
    } else if (lic.assignment_type === 'per_device') {
      if (!assignForm.asset_id) { setError('Select an asset.'); return; }
      payload.asset_id = parseInt(assignForm.asset_id);
    } else { // mixed
      if (!assignForm.employee_id && !assignForm.asset_id) { setError('Select an employee or asset.'); return; }
      if (assignForm.employee_id) payload.employee_id = parseInt(assignForm.employee_id);
      if (assignForm.asset_id)    payload.asset_id    = parseInt(assignForm.asset_id);
    }
    setSaving(true);
    try {
      await it.assignLicense(lic.id, payload);
      setAssignModal(null); setAssignForm(EMPTY_ASSIGN); load();
      // Refresh detail if open
      if (detailModal?.id === lic.id) {
        const r = await it.getLicense(lic.id); setDetailModal(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to assign license.');
    } finally { setSaving(false); }
  };

  // ── Revoke ────────────────────────────────────────────────────────────────
  const handleRevoke = async (lic, assignmentId) => {
    if (!window.confirm('Revoke this license assignment?')) return;
    try {
      await it.revokeLicense(lic.id, assignmentId);
      load();
      if (detailModal?.id === lic.id) {
        const r = await it.getLicense(lic.id); setDetailModal(r.data);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to revoke.');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (lic) => {
    if (!window.confirm(`Delete "${lic.name}"? This cannot be undone.`)) return;
    try {
      await it.deleteLicense(lic.id); load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete license.');
    }
  };

  // ── Open assign modal ─────────────────────────────────────────────────────
  const openAssign = (lic) => {
    setError('');
    setAssignForm({ ...EMPTY_ASSIGN });
    setAssignModal(lic);
  };

  // ── Open detail modal ─────────────────────────────────────────────────────
  const openDetail = async (lic) => {
    const r = await it.getLicense(lic.id).catch(() => ({ data: lic }));
    setDetailModal(r.data);
  };

  // ── Open edit modal ───────────────────────────────────────────────────────
  const openEdit = (lic) => {
    setError('');
    setForm({
      name:            lic.name || '',
      vendor:          lic.vendor || '',
      license_type:    lic.license_type || 'subscription',
      assignment_type: lic.assignment_type || 'per_user',
      license_key:     lic.license_key || '',
      seats_total:     lic.seats_total || 1,
      purchase_date:   lic.purchase_date || '',
      expiry_date:     lic.expiry_date || '',
      cost:            lic.cost ?? '',
      notes:           lic.notes || '',
      status:          lic.status || 'active',
    });
    setFormModal(lic);
  };

  const today = new Date();
  const isExpiringSoon = (d) => { if (!d) return false; const diff = (new Date(d) - today) / 86400000; return diff >= 0 && diff <= 30; };
  const isExpired = (d) => d && new Date(d) < today;

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      header: 'Product / Vendor',
      key: 'name',
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</div>
          {row.vendor && <div style={{ fontSize: 12, color: theme.textMuted }}>{row.vendor}</div>}
        </div>
      ),
    },
    {
      header: 'Type',
      key: 'license_type',
      render: (_, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={s.pill(theme.textMuted)}>{fmt(row.license_type)}</span>
          <span style={s.pill(ASSIGNMENT_TYPE_COLORS[row.assignment_type])}>{fmt(row.assignment_type)}</span>
        </div>
      ),
    },
    {
      header: 'Seats',
      key: 'seats_used',
      render: (_, row) => s.seatBar(row.seats_used, row.seats_total),
    },
    {
      header: 'Expiry',
      key: 'expiry_date',
      render: (_, row) => {
        if (!row.expiry_date) return <span style={{ color: theme.textMuted }}>—</span>;
        const expired = isExpired(row.expiry_date);
        const soon    = isExpiringSoon(row.expiry_date);
        return (
          <span style={{ color: expired ? theme.danger : soon ? theme.warning : theme.text, fontWeight: (expired || soon) ? 700 : 400 }}>
            {fmtDate(row.expiry_date)}{soon && !expired && ' ⚠️'}{expired && ' ❌'}
          </span>
        );
      },
    },
    {
      header: 'Cost',
      key: 'cost',
      render: (_, row) => fmtCurrency(row.cost),
    },
    {
      header: 'Status',
      key: 'status',
      render: (_, row) => <span style={s.pill(STATUS_COLORS[row.status] || theme.textMuted)}>{fmt(row.status)}</span>,
    },
    {
      header: 'Actions',
      key: 'id',
      align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button size="sm" variant="secondary" onClick={() => openDetail(row)}>Details</Button>
          <Button size="sm" variant="primary"
            disabled={row.seats_available === 0 || row.status !== 'active'}
            onClick={() => openAssign(row)}>
            Assign
          </Button>
          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  // ── Assign modal: which fields to show ────────────────────────────────────
  const showEmployee = assignModal && ['per_user', 'mixed'].includes(assignModal.assignment_type);
  const showAsset    = assignModal && ['per_device', 'mixed'].includes(assignModal.assignment_type);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.text, margin: 0 }}>License Management</h1>
          <p style={{ color: theme.textMuted, fontSize: 13, margin: '4px 0 0' }}>{licenses.length} license(s) found</p>
        </div>
        <Button variant="primary" onClick={() => { setError(''); setForm(EMPTY_FORM); setFormModal({}); }}>
          + Add License
        </Button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...s.input, width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {ASSIGNMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select style={{ ...s.input, width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{fmt(s)}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {ASSIGNMENT_TYPES.map(t => {
          const group = licenses.filter(l => l.assignment_type === t.value);
          const totalSeats = group.reduce((a, l) => a + l.seats_total, 0);
          const usedSeats  = group.reduce((a, l) => a + l.seats_used,  0);
          return (
            <Card key={t.value}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, color: ASSIGNMENT_TYPE_COLORS[t.value] }}>{t.label} Licenses</div>
                  <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 2 }}>{t.desc}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: theme.text }}>{group.length}</div>
                  <div style={{ fontSize: 11, color: theme.textMuted }}>{usedSeats}/{totalSeats} seats</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Table */}
      <Card padding="0">
        <Table columns={columns} data={licenses} loading={loading} emptyMessage="No licenses found" />
      </Card>

      {/* ── ADD / EDIT MODAL ── */}
      <Modal
        isOpen={!!formModal}
        onClose={() => setFormModal(null)}
        title={formModal?.id ? 'Edit License' : 'Add License'}
        maxWidth="620px"
        footer={
          <>
            <Button variant="outlineGray" onClick={() => setFormModal(null)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </>
        }
      >
        {error && <div style={{ color: theme.danger, marginBottom: 10, fontSize: 13 }}>{error}</div>}

        <div style={s.field}>
          <label style={s.label}>Product Name *</label>
          <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Microsoft Office 365, Oracle Database…" />
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Vendor</label>
            <input style={s.input} value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Microsoft, Oracle, Adobe…" />
          </div>
          <div style={s.field}>
            <label style={s.label}>License Type</label>
            <select style={s.input} value={form.license_type} onChange={e => setForm(f => ({ ...f, license_type: e.target.value }))}>
              {LICENSE_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
            </select>
          </div>
        </div>

        {/* Assignment type with description */}
        <div style={s.field}>
          <label style={s.label}>Assignment Type *</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ASSIGNMENT_TYPES.map(t => (
              <label key={t.value} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                padding: '8px 12px', borderRadius: 8,
                border: `1.5px solid ${form.assignment_type === t.value ? ASSIGNMENT_TYPE_COLORS[t.value] : theme.border}`,
                background: form.assignment_type === t.value ? ASSIGNMENT_TYPE_COLORS[t.value] + '11' : 'transparent',
              }}>
                <input type="radio" name="assignment_type" value={t.value} checked={form.assignment_type === t.value}
                  onChange={() => setForm(f => ({ ...f, assignment_type: t.value }))} style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: form.assignment_type === t.value ? ASSIGNMENT_TYPE_COLORS[t.value] : theme.text }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Total Seats *</label>
            <input style={s.input} type="number" min={1} value={form.seats_total}
              onChange={e => setForm(f => ({ ...f, seats_total: e.target.value }))} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Cost (₹)</label>
            <input style={s.input} type="number" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="0" />
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>License Key</label>
          <input style={s.input} value={form.license_key} onChange={e => setForm(f => ({ ...f, license_key: e.target.value }))} placeholder="XXXXX-XXXXX-XXXXX" />
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Purchase Date</label>
            <input style={s.input} type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Expiry Date</label>
            <input style={s.input} type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>Notes</label>
          <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        {formModal?.id && (
          <div style={s.field}>
            <label style={s.label}>Status</label>
            <select style={s.input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {STATUSES.map(s => <option key={s} value={s}>{fmt(s)}</option>)}
            </select>
          </div>
        )}
      </Modal>

      {/* ── DETAIL MODAL ── */}
      <Modal
        isOpen={!!detailModal}
        onClose={() => setDetailModal(null)}
        title={detailModal?.name || 'License Details'}
        maxWidth="700px"
      >
        {detailModal && (
          <div>
            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 16 }}>
              {[
                ['Vendor',          detailModal.vendor || '—'],
                ['License Type',    fmt(detailModal.license_type)],
                ['Assignment Type', <span style={s.pill(ASSIGNMENT_TYPE_COLORS[detailModal.assignment_type])}>{fmt(detailModal.assignment_type)}</span>],
                ['Status',          <span style={s.pill(STATUS_COLORS[detailModal.status])}>{fmt(detailModal.status)}</span>],
                ['Seats',           `${detailModal.seats_used} used / ${detailModal.seats_total} total (${detailModal.seats_available} available)`],
                ['Cost',            fmtCurrency(detailModal.cost)],
                ['Purchase Date',   fmtDate(detailModal.purchase_date)],
                ['Expiry Date',     fmtDate(detailModal.expiry_date)],
              ].map(([k, v]) => (
                <div key={k}><span style={{ fontSize: 12, color: theme.textMuted, fontWeight: 600 }}>{k}: </span><span style={{ fontSize: 13 }}>{v}</span></div>
              ))}
            </div>

            {detailModal.license_key && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: theme.bg, borderRadius: 6, fontFamily: 'monospace', fontSize: 13 }}>
                🔑 {detailModal.license_key}
              </div>
            )}
            {detailModal.notes && (
              <div style={{ marginBottom: 12, fontSize: 13, color: theme.textMuted }}>{detailModal.notes}</div>
            )}

            {/* Seat usage bar */}
            <div style={{ marginBottom: 16 }}>
              {s.seatBar(detailModal.seats_used, detailModal.seats_total)}
            </div>

            {/* Active assignments */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 14 }}>Active Assignments ({(detailModal.active_assignments || []).length})</strong>
              {detailModal.seats_available > 0 && detailModal.status === 'active' && (
                <Button size="sm" onClick={() => { setDetailModal(null); openAssign(detailModal); }}>+ Assign</Button>
              )}
            </div>

            {(detailModal.active_assignments || []).length === 0 ? (
              <div style={{ color: theme.textMuted, fontSize: 13, padding: '8px 0' }}>No active assignments.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {detailModal.active_assignments.map(a => (
                  <div key={a.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: theme.bg, borderRadius: 8, padding: '8px 12px',
                  }}>
                    <div>
                      {a.employee_name && (
                        <div style={{ fontWeight: 500, fontSize: 13 }}>👤 {a.employee_name}</div>
                      )}
                      {a.asset_name && (
                        <div style={{ fontWeight: 500, fontSize: 13 }}>💻 {a.asset_name} <span style={{ color: theme.textMuted, fontSize: 11 }}>({a.asset_code})</span></div>
                      )}
                      <div style={{ fontSize: 11, color: theme.textMuted }}>Assigned {fmtDate(a.assigned_date)}{a.notes ? ` · ${a.notes}` : ''}</div>
                    </div>
                    <Button size="xs" variant="danger" onClick={() => handleRevoke(detailModal, a.id)}>Revoke</Button>
                  </div>
                ))}
              </div>
            )}

            {/* History (revoked) */}
            {(detailModal.all_assignments || []).filter(a => a.revoked_date).length > 0 && (
              <>
                <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, fontSize: 13, color: theme.textMuted }}>
                  Assignment History
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {detailModal.all_assignments.filter(a => a.revoked_date).map(a => (
                    <div key={a.id} style={{ fontSize: 12, color: theme.textMuted, padding: '4px 8px', borderLeft: `2px solid ${theme.border}`, paddingLeft: 10 }}>
                      {a.employee_name ? `👤 ${a.employee_name}` : `💻 ${a.asset_name}`} · Assigned {fmtDate(a.assigned_date)} → Revoked {fmtDate(a.revoked_date)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── ASSIGN MODAL ── */}
      <Modal
        isOpen={!!assignModal}
        onClose={() => { setAssignModal(null); setError(''); }}
        title={`Assign: ${assignModal?.name}`}
        maxWidth="480px"
        footer={
          <>
            <Button variant="outlineGray" onClick={() => setAssignModal(null)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleAssign}>Assign</Button>
          </>
        }
      >
        {assignModal && (
          <div>
            {/* Seat usage reminder */}
            <div style={{ marginBottom: 12, padding: '8px 12px', background: theme.bg, borderRadius: 6, fontSize: 13 }}>
              <span style={s.pill(ASSIGNMENT_TYPE_COLORS[assignModal.assignment_type])}>{fmt(assignModal.assignment_type)}</span>
              <span style={{ marginLeft: 10, color: theme.textMuted }}>
                {assignModal.seats_available} of {assignModal.seats_total} seat(s) available
              </span>
            </div>

            {error && <div style={{ color: theme.danger, marginBottom: 10, fontSize: 13 }}>{error}</div>}

            {showEmployee && (
              <div style={s.field}>
                <label style={s.label}>
                  Assign to Employee {assignModal.assignment_type === 'mixed' && <span style={{ color: theme.textMuted }}>(or choose asset below)</span>}
                </label>
                <SearchableSelect
                  options={employees.map(e => ({ value: e.id, label: e.full_name, meta: e.emp_code || e.department_name || '' }))}
                  value={assignForm.employee_id}
                  onChange={v => setAssignForm(f => ({ ...f, employee_id: v ? String(v) : '', asset_id: '' }))}
                  placeholder="— Select employee —"
                />
              </div>
            )}

            {assignModal.assignment_type === 'mixed' && (
              <div style={{ textAlign: 'center', color: theme.textMuted, fontSize: 12, marginBottom: 8 }}>— OR —</div>
            )}

            {showAsset && (
              <div style={s.field}>
                <label style={s.label}>
                  Assign to Asset {assignModal.assignment_type === 'mixed' && <span style={{ color: theme.textMuted }}>(or choose employee above)</span>}
                </label>
                <select style={s.input} value={assignForm.asset_id}
                  onChange={e => setAssignForm(f => ({ ...f, asset_id: e.target.value, employee_id: '' }))}>
                  <option value="">— Select asset —</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.asset_code})</option>
                  ))}
                </select>
              </div>
            )}

            <div style={s.grid2}>
              <div style={s.field}>
                <label style={s.label}>Assignment Date *</label>
                <input style={s.input} type="date" value={assignForm.assigned_date}
                  onChange={e => setAssignForm(f => ({ ...f, assigned_date: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Notes</label>
                <input style={s.input} value={assignForm.notes}
                  onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional note" />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Licenses;
